import React, { useEffect, useRef, useState } from "react";

import { Grid, Box } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { decodeToken, isExpired } from "react-jwt";
import { useHistory } from "react-router-dom";

import { NotificationToast } from "../../../components/Generic/Toast/NotificationToast";
import { PublicFooter } from "../../../components/PublicFooter";
import AuthLeftPanel from "../../../components/AuthLeftPanel";
import { PUBLIC_ROUTES } from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import { changeTitlePageByResource } from "../../../helpers/helpers";
import { Decoded } from "../../../models/decoded";
import UserService from "../../../services/user.service";
import { checkError } from "../../../util/checkError";
import ForgotPasswordComponent, { ForgotPasswordHandle } from "./components/Form";
import useStyles from "./styles";

interface User {
  id: string | number;
  email: string;
}
interface HistoryState {
  token: string;
  user: User;
}

const ForgotPassword = () => {
  const history = useHistory<HistoryState>();
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const forgotPasswordRef = useRef<ForgotPasswordHandle>(null);

  const { currentUser } = useAppState();

  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [tokenIsExpired, setTokenIsExpired] = useState<boolean>(false);
  const [rightSideLoaded, setRightSideLoaded] = useState(false);

  const [tokenToRecoverPassword, setTokenToRecoverPassword] = useState<string>("");

  useEffect(() => {
    // Left side loads first, then right side after delay
    const timer = setTimeout(() => {
      setRightSideLoaded(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const { mutate: forgotPassword, isLoading } = useMutation(
    ["users.forgot.password"],
    ({ email }: { email: string }) => UserService.forgotPassword(email),
    {
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("couldNotSendEmail"));
      },
    }
  );

  useEffect(() => {
    changeTitlePageByResource(translate("forgotPassword"));
  }, []);

  useEffect(() => {
    try {
      if (!tokenToRecoverPassword) {
        const url: string = window.location.href;
        const parts = url.split("forgot?");

        const token = parts.length === 2 ? parts[1] : "";

        if (token) {
          const decodedToken = decodeToken<Decoded>(token);
          const tokenIsExpired = isExpired(token);

          if (!decodedToken || tokenIsExpired) {
            setTokenIsExpired(true);
            if (decodedToken && decodedToken?.email) {
              forgotPasswordRef?.current?.setEmail(decodedToken?.email);
            } else {
              NotificationToast.error(translate("thereWasAnErrorCapturingYourEmailToResend"));
            }
          } else {
            setTokenToRecoverPassword(token);
            history.replace(PUBLIC_ROUTES.UPDATE_PASSWORD, {
              token,
              user: decodedToken,
            });
          }
        } else if (currentUser) {
          history.replace("/");
        }
      }
    } catch (error) {
      history.replace(PUBLIC_ROUTES.FORGOT);
    }
  }, []);

  const handleSubmit = (event: React.FormEvent, tokenIsExpired?: boolean) => {
    event.preventDefault();
    forgotPassword(
      { email: forgotPasswordRef?.current?.email ?? "" },
      {
        onSuccess: (response) => {
          if (response.data) {
            if (tokenIsExpired) {
              NotificationToast.success(`${translate("emailSuccessfullyResent")}`);
              setEmailSent(true);
              setTokenIsExpired(false);
            } else {
              NotificationToast.success(
                `${translate("ifAMatchingAccountWasFoundAnEmailWasSentTo")} ${
                  forgotPasswordRef?.current?.email
                } ${translate("toAllowYouToResetYourPassword")}`
              );
              setEmailSent(true);
            }
          }
        },
      }
    );
  };

  return (
    <div className={classes.mainDiv}>
      <Grid className={classes.container} container direction="row">
        <Grid item xs={12} sm={6}>
          <Box className={classes.leftBox}>
            <AuthLeftPanel showContentDelay={0} />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            className={classes.rightBox}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              opacity: rightSideLoaded ? 1 : 0,
              transition: "opacity 0.6s ease-in-out",
              position: "relative",
            }}
          >
            <Box sx={{ width: "100%", maxWidth: "520px", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box sx={{ width: "100%", display: "flex", alignItems: "flex-start", justifyContent: "flex-start" }}>
                <ForgotPasswordComponent
                  ref={forgotPasswordRef}
                  emailSent={emailSent}
                  tokenIsExpired={tokenIsExpired}
                  isLoading={isLoading}
                  handleSubmit={handleSubmit}
                />
              </Box>
            </Box>
            <Box sx={{ width: "100%", maxWidth: "520px", pb: 2, display: "flex", justifyContent: "center" }}>
              <PublicFooter />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
};

export default ForgotPassword;
